import React from 'react';
import { VehicleTableProps, VehicleSortKey } from '@/types';
import { VehicleRow } from './VehicleRow';
import { useAppContext } from '@/context/AppContext';

export const VehicleTable = React.memo<VehicleTableProps>(({ 
  vehicles, 
  onRemoveVehicle,
  groupId = 'default',
  selectedVehicles = new Set(),
  onSelectionChange,
  onSelectAll,
  sortKey,
  sortDirection,
  onSortChange
}) => {
  const { state } = useAppContext();

  if (vehicles.length === 0) {
    return null;
  }

  const allSelected = vehicles.length > 0 && vehicles.every(v => selectedVehicles.has(v.modelName));
  const someSelected = vehicles.some(v => selectedVehicles.has(v.modelName));

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll?.(event.target.checked);
  };

  const renderSortHeader = (label: string, key: VehicleSortKey) => {
    const isActive = sortKey === key;
    const indicator = isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '↕';
    return (
      <button
        type="button"
        className={`sort-button ${isActive ? 'active' : ''}`}
        onClick={() => onSortChange(key)}
      >
        <span>{label}</span>
        <span className="sort-indicator">{indicator}</span>
      </button>
    );
  };

  return (
    <table className="vehicles-table">
      <thead>
        <tr>
          <th className="checkbox-header">
            <input
              type="checkbox"
              checked={allSelected}
              ref={checkbox => {
                if (checkbox) {
                  checkbox.indeterminate = someSelected && !allSelected;
                }
              }}
              onChange={handleSelectAllChange}
              className="select-all-checkbox"
              aria-label="Select all vehicles"
            />
          </th>
          <th>{renderSortHeader('Model', 'model')}</th>
          <th>{renderSortHeader('Brand', 'brand')}</th>
          <th>{renderSortHeader('Name', 'name')}</th>
          <th>{renderSortHeader('Category', 'category')}</th>
          <th>{renderSortHeader('Price', 'price')}</th>
          <th>{renderSortHeader('Type', 'type')}</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((vehicleData, index) => (
          <VehicleRow
            key={`${groupId}-${vehicleData.modelName}-${index}`}
            vehicleData={vehicleData}
            onRemove={() => onRemoveVehicle(vehicleData.modelName)}
            isSelected={selectedVehicles.has(vehicleData.modelName)}
            onSelectionChange={(selected) => onSelectionChange?.(vehicleData.modelName, selected)}
            searchQuery={state.ui.searchQuery}
          />
        ))}
      </tbody>
    </table>
  );
});

VehicleTable.displayName = 'VehicleTable';
